import { Link, useParams } from "react-router-dom";
import { articleSchema, breadcrumb, useSEO } from "../lib/seo";
import { ASSETS, SITE } from "../lib/site";
import PageHero from "../components/PageHero";
import { findArticle, getArticles } from "../data/articles";
import { trackEvent } from "../lib/analytics";
import { ArrowLeft, Calendar, Clock, Tag } from "lucide-react";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = findArticle(slug);

  useSEO({
    title: article ? `${article.title} | Sloto69` : "Artikel Tidak Ditemukan | Sloto69",
    description: article?.excerpt || "Artikel tidak ditemukan.",
    keywords: article?.keywords || "sloto69 artikel",
    canonical: `${SITE.baseUrl}/artikel/${slug || ""}`,
    image: article?.imageUrl || `${SITE.baseUrl}${ASSETS.ogImage}`,
    robots: article ? "index, follow, max-image-preview:large" : "noindex, follow",
    jsonLd: article
      ? [
          breadcrumb([
            { name: "Beranda", url: `${SITE.baseUrl}/` },
            { name: "Artikel", url: `${SITE.baseUrl}/artikel` },
            { name: article.title, url: `${SITE.baseUrl}/artikel/${article.slug}` },
          ]),
          articleSchema({
            article,
            site: SITE,
            logo: ASSETS.logo,
            image: article.imageUrl || `${SITE.baseUrl}${ASSETS.ogImage}`,
          }),
        ]
      : null,
  });

  if (!article) {
    return (
      <section className="section-pad">
        <div className="shell text-center" data-testid="article-not-found">
          <h1 className="font-display text-3xl">Artikel Tidak Ditemukan</h1>
          <p className="text-slate-400 mt-3">
            Artikel yang Anda cari tidak tersedia. Silakan kembali ke halaman artikel.
          </p>
          <Link to="/artikel" className="btn-primary mt-6 inline-flex">
            <ArrowLeft className="w-4 h-4" /> Kembali ke Artikel
          </Link>
        </div>
      </section>
    );
  }

  const related = getArticles().filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <>
      <PageHero
        eyebrow={article.category}
        title={article.title}
        description={article.excerpt}
        breadcrumb={[
          { label: "Artikel", to: "/artikel" },
          { label: article.title },
        ]}
      />

      <section className="section-pad" data-testid="article-detail">
        <div className="shell grid lg:grid-cols-12 gap-12">
          <article className="lg:col-span-8">
            {article.imageUrl && (
              <img
                src={article.imageUrl}
                alt={article.title}
                className="mb-8 aspect-[16/9] w-full rounded-lg border border-white/10 object-cover"
              />
            )}

            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 mb-8">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> {article.publishedAt}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> {article.readTime}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-[#D4AF37]" /> {article.category}
              </span>
            </div>

            <div
              className="prose-sloto"
              dangerouslySetInnerHTML={{ __html: article.body }}
            />

            {related.length > 0 && (
              <section className="mt-10 rounded-lg border border-white/10 bg-white/[0.03] p-5">
                <h2 className="font-display text-xl font-semibold">Baca juga</h2>
                <ul className="mt-4 grid gap-3 text-sm text-slate-300">
                  {related.map((item) => (
                    <li key={item.slug}>
                      <Link to={`/artikel/${item.slug}`} className="text-[#10B981] hover:text-white">
                        {item.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="mt-12 card-premium p-6 flex flex-wrap gap-4 justify-between items-center">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Bantuan</p>
                <h3 className="font-display text-xl mt-1">Butuh informasi lebih lanjut?</h3>
              </div>
              <Link to="/kontak" data-testid="article-contact-cta" className="btn-primary">
                Hubungi Tim Bantuan
              </Link>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 grid gap-4">
              <div className="card-premium p-6">
                <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Artikel Lainnya</p>
                <ul className="mt-4 grid gap-4">
                  {related.map((r, i) => (
                    <li key={r.slug}>
                      <Link
                        to={`/artikel/${r.slug}`}
                        data-testid={`related-article-${i}`}
                        onClick={() =>
                          trackEvent("read_article", {
                            event_category: "content",
                            event_label: r.title,
                            article_slug: r.slug,
                            location: "related_article",
                          })
                        }
                        className="block group"
                      >
                        <h4 className="font-display text-base font-semibold text-white group-hover:text-[#10B981] transition">
                          {r.title}
                        </h4>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2">{r.excerpt}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <Link to="/artikel" data-testid="article-back-to-list" className="btn-secondary justify-center">
                <ArrowLeft className="w-4 h-4" /> Semua Artikel
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
